// SZEMÉLY-OPCIÓK locale-tudatos alakja és SORRENDJE a legördülőkhöz/pickerekhez.
//
// MIÉRT KELL: a személy-nevek megjelenítési alakja NYELV-FÜGGŐ (magyarul „Reizner Szilárd", angolul
// „Szilárd Reizner" — lásd `formatName`), és az ABC-rendezés a MEGJELENÍTETT alakot követi, nem a tárolt
// `displayName`-t. A BFF ezért csak stabil alap-sorrendet ad (`hu` collation), a végleges alakot és rendet a
// kliens dönti el — ott van a nyelv, és ott váltható futásidőben (ugyanaz a cache-elt válasz szolgál ki
// mindkét nyelvet). Nélküle a magyar felület angol sorrendű, a nyelvváltás után pedig ROSSZ ABC-rendű
// listát adott (mérve: a munkatárs/kapcsolattartó legördülők).
//
// A rendezés `localeCompare(locale)`: a magyar collation az `ö`-t az `o` UTÁN sorolja (nem a `z` után) — ezt
// egy sima `<` összehasonlítás elhibázza.
import { formatName, type NameParts } from './format-name';

/** A BFF `PersonOptionSchema`-jának kliens-oldali alakja (id + fallback név + strukturált név-részek). */
export interface PersonOptionInput extends NameParts {
  id: string;
  /** A locale-független fallback (displayName/e-mail) — ha a strukturált részek hiányoznak. */
  name: string;
}

/** Egy kész opció: a legördülők `{ id, name }` alakja, ahol a `name` MÁR a nyelvhelyes teljes név. */
export interface PersonOption {
  id: string;
  name: string;
}

/**
 * Személy-opciók listája → nyelvhelyes névvel formázva ÉS az adott nyelv collationjával ABC-rendbe rakva.
 * A hívó a nyers BFF-listát adja át (a `name` a fallback), és kész `{ id, name }` opciókat kap.
 */
export function toPersonOptions(
  items: readonly PersonOptionInput[],
  locale: string,
): PersonOption[] {
  return items
    .map((p) => ({
      id: p.id,
      name: formatName(
        { givenName: p.givenName, familyName: p.familyName, displayName: p.name },
        locale,
      ),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
}

/**
 * Ugyanaz, de a hívó EXTRA mezőit megtartja (pl. az igénylő-opció e-mailje) — a `name` a nyelvhelyes alakra
 * cserélődik, a rendezés szintén a megjelenített név szerint megy.
 */
export function toPersonOptionsWith<T extends PersonOptionInput>(
  items: readonly T[],
  locale: string,
): (T & { name: string })[] {
  return items
    .map((p) => ({
      ...p,
      name: formatName(
        { givenName: p.givenName, familyName: p.familyName, displayName: p.name },
        locale,
      ),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
}
